package com.essntl.features.invoice.ui.addeditinvoice.components

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.essntl.features.invoice.ui.addeditinvoice.AddEditInvoiceEvent
import com.essntl.features.invoice.ui.addeditinvoice.AddEditInvoiceNavigation
import com.essntl.features.invoice.ui.addeditinvoice.AddEditInvoiceState
import com.essntl.features.invoice.ui.addeditinvoice.AddEditInvoiceValidation
import com.essntl.features.invoice.ui.addeditinvoice.components.content.AddEditInvoiceDetails
import com.essntl.features.invoice.ui.addeditinvoice.components.content.AddEditInvoiceHeader
import com.essntl.features.invoice.ui.addeditinvoice.components.content.AddEditInvoicePaymentLinks
import com.essntl.features.invoice.ui.addeditinvoice.components.content.day.InvoicingDaySection
import com.essntl.features.invoice.ui.addeditinvoice.components.content.day.InvoicingDayUiState
import com.essntl.features.invoice.ui.addeditinvoice.components.content.day.rememberAddEditItineraryServiceList
import com.essntl.features.invoice.ui.addeditinvoice.components.content.day.rememberAddEditProposalServiceList
import com.essntl.features.invoice.ui.addeditinvoice.components.content.extraservice.InvoicingExtraServiceSection
import com.essntl.features.invoice.ui.rememberDayProposalServiceList
import com.essntl.features.invoice.ui.rememberItineraryServiceList
import com.essntl.features.invoice.ui.rememberLocalDate
import com.essntl.uikit.components.scroll.EsVerticalScrollbar
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberNumberOfDays
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun AddEditInvoiceContent(
    state: AddEditInvoiceState,
    validation: AddEditInvoiceValidation,
    onEvent: (AddEditInvoiceEvent) -> Unit,
    onNavigation: (AddEditInvoiceNavigation) -> Unit,
    modifier: Modifier = Modifier,
) {
    val scrollState = rememberScrollState()

    val itinerary = state.getItinerary.data

    val proposal = state.getProposal.data ?: itinerary?.proposal

    val invoiceId = state.getInvoice.data?.id

    Column(
        verticalArrangement = Arrangement.spacedBy(1.spacing),
        modifier = modifier,
    ) {

        AddEditInvoiceHeader(
            state = state,
            onEvent = onEvent,
            onNavigation = onNavigation,
        )

        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f),
        ) {
            if (state.isLoading) {
                EsSpinner()

                return@Box
            }

            if (state.isFailure ||
                proposal == null
            ) {
                Text(
                    text = "Something goes wrong!",
                    color = MaterialTheme.esColorScheme.textSecondary,
                    style = MaterialTheme.esTypography.p1Medium16,
                )

                return@Box
            }

            val numberOfDays = rememberNumberOfDays(proposal.fromDate, proposal.toDate)

            Column(
                verticalArrangement = Arrangement.spacedBy(1.spacing),
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(
                        state = scrollState,
                    ),
            ) {
                AddEditInvoiceDetails(
                    state = state,
                    validation = validation,
                    onEvent = onEvent,
                )

                if (!invoiceId.isNullOrEmpty())
                    AddEditInvoicePaymentLinks(
                        state = state,
                        deactivatePaymentLink = { paymentLink, active ->
                            onEvent(
                                AddEditInvoiceEvent.DeactivatePaymentLink(
                                    paymentLink = paymentLink,
                                    active = active,
                                ),
                            )
                        },
                        addPaymentLink = { amount, paymentParameterIds ->
                            onEvent(
                                AddEditInvoiceEvent.AddPaymentLink(
                                    amount = amount,
                                    paymentParameterIds = paymentParameterIds,
                                ),
                            )
                        },
                    )

                if (state.extraServiceList.data
                        .orEmpty()
                        .isNotEmpty()
                )
                    InvoicingExtraServiceSection(
                        state = state,
                        onRemoveExtraService = {
                            onEvent(
                                AddEditInvoiceEvent.RemoveExtraService(
                                    id = it.id,
                                ),
                            )
                        },
                    )

                if (numberOfDays != null && numberOfDays >= 0)
                    repeat(numberOfDays) { index ->
                        val localDate = rememberLocalDate(index, proposal.fromDate)

                        val dayProposalServiceList = rememberDayProposalServiceList(
                            localDate,
                            state.getAllProposalServices,
                        )

                        val dayItineraryServiceList = rememberItineraryServiceList(
                            localDate,
                            state.getAllItineraryServices,
                        )

                        val serviceUiStateList =
                            if (itinerary != null)
                                rememberAddEditItineraryServiceList(
                                    dayItineraryServiceList,
                                    state.getInvoiceItineraryServices.data.orEmpty(),
                                    state.getItineraryServices.data.orEmpty(),
                                    state.getAllCategory.data,
                                    state.checkedServiceIds,
                                )
                            else
                                rememberAddEditProposalServiceList(
                                    dayProposalServiceList,
                                    state.getInvoiceProposalServices.data.orEmpty(),
                                    state.getProposalServices.data.orEmpty(),
                                    state.getAllCategory.data,
                                    state.checkedServiceIds,
                                )

                        val invoicingDayUiState = remember(
                            index,
                            serviceUiStateList,
                        ) {
                            InvoicingDayUiState(
                                index = index,
                                serviceList = serviceUiStateList,
                            )
                        }

                        if (invoicingDayUiState.serviceList.isNotEmpty())
                            InvoicingDaySection(
                                state = invoicingDayUiState,
                                fromDate = proposal.fromDate,
                                onServiceCheckChanged = { id, checked ->
                                    onEvent(
                                        AddEditInvoiceEvent.CheckService(
                                            id = id,
                                            isChecked = checked,
                                        ),
                                    )
                                },
                                onCheckAllServices = { checked ->
                                    onEvent(
                                        AddEditInvoiceEvent.CheckDayServices(
                                            date = localDate.toString(),
                                            isChecked = checked,
                                        ),
                                    )
                                },
                            )
                    }
            }

            EsVerticalScrollbar(
                scrollState = scrollState,
                modifier = Modifier
                    .align(Alignment.CenterEnd)
                    .fillMaxHeight(),
            )
        }
    }
}